import { t } from "@lingui/macro";
import graphql from "babel-plugin-relay/macro";
import { format } from "date-fns";
import { useAtomValue, useSetAtom } from "jotai";
import { useState } from "react";
import { useFragment } from "react-relay";

import { Button } from "@whoosh/design-system/src/components/controls";
import {
  EditIcon,
  KebabVerticalIcon,
  LogOutIcon,
  TrashIcon,
} from "@whoosh/design-system/src/components/icons_v2/general";
import { Menu } from "@whoosh/design-system/src/components/overlay/Menu";
import { PopUpModal } from "@whoosh/design-system/src/components/overlay/PopUpModal";
import { Text, VStack } from "@whoosh/design-system/src/components/primitive";
import { EventSignUpProcessSteps } from "@whoosh/features/src/Events/features/AddorEditSignUp";
import { mergeEligibleWindowCharges } from "@whoosh/features/src/Events/utils/mergeEligibleWindowCharges";
import { convertServerTimeToLocal } from "@whoosh/utilities/src/dates";

import { currentProfileAtom } from "../../../../features/Club/atoms";
import { openEventSignUpModalAtom } from "../../atoms/actions/openEventSignUpModal/openEventSignUpModalAtom";
import { EventDetailEventHasFormAtom } from "../../atoms/eventForm";
import { useDeleteEventSignUp } from "../../hooks/useDeleteEventSignUp";
import { EventSignUpRow } from "../../types";
import { useCurrentSignUp } from "../useCurrentSignUp/useCurrentSignUp";
import { EventDetailActions_event$key } from "./__generated__/EventDetailActions_event.graphql";
import { useRemoveCurrentUserFromEvent } from "./hooks/useRemoveCurrentUserFromEvent";

type EventDetailActionsProps = {
  fragmentRef: EventDetailActions_event$key;
};

export const EventDetailActions = ({
  fragmentRef,
}: EventDetailActionsProps) => {
  const event = useFragment(EventDetailActionFragmentString, fragmentRef);
  const [isLeaveEventModalOpen, setIsLeaveEventModalOpen] = useState(false);
  const [isRemoveMeFromEventModalOpen, setIsRemoveMeFromEventModalOpen] =
    useState(false);
  const openEventSignUpModal = useSetAtom(openEventSignUpModalAtom);
  const [isMoreMenuOpen, setIsMoreMenuOpen] = useState(false);

  const accessStatusForClient = event.accessStatusForClient;

  const currentProfile = useAtomValue(currentProfileAtom);
  const registrantId = currentProfile?.memberId;

  const currentSignUp = useCurrentSignUp(event);

  const currentSignupWindow = currentSignUp?.signUpWindow;

  const [deleteEventSignUp] = useDeleteEventSignUp();

  const handleDeleteRegistration = () => {
    if (currentSignUp?.id && registrantId) {
      deleteEventSignUp(
        {
          eventSignUpId: currentSignUp.id,
        },
        registrantId
      );
      setIsLeaveEventModalOpen(false);
    }
  };

  const handleRemoveFromSignUpSuccess = () => {
    setIsRemoveMeFromEventModalOpen(false);
  };

  const [removeCurrentPlayer] = useRemoveCurrentUserFromEvent(
    handleRemoveFromSignUpSuccess
  );

  const handleRemoveFromSignUp = () => {
    if (currentSignUp) {
      const input: EventSignUpRow = {
        id: currentSignUp?.id ?? "",
        waitlistIndex: currentSignUp?.waitlistIndex ?? 0,
        checkedIn: currentSignUp?.checkedIn,
        ownerClient: currentSignUp.ownerClient?.id
          ? {
              type: currentSignUp.ownerClient.memberOwnerId
                ? "MEMBER"
                : "CUSTOMER",
              id: currentSignUp.ownerClient.id,
              firstName: currentSignUp.ownerClient.firstName ?? "",
              lastName: currentSignUp.ownerClient.lastName ?? "",
              profileImagePublicId:
                currentSignUp.ownerClient.profileImage?.publicId,
            }
          : undefined,
        registrations:
          currentSignUp.registrations?.edges?.flatMap((e) =>
            e?.node?.id
              ? [
                  {
                    id: e.node.id,
                    index: e.node.index === undefined ? null : e.node.index,
                    type: e.node.type === undefined ? null : e.node.type,
                    payingMemberId:
                      e.node.payingMember?.id ?? e.node.registrant?.id ?? "",
                    primaryRegistrationChargeTypeId:
                      e.node.primaryChargeType?.id ?? null,
                    registrant: {
                      ...e.node.registrant,
                      playerId: e.node.registrant?.id,
                      phoneNumber:
                        e.node.registrant?.phoneNumber?.nationalNumber ?? null,
                    },
                    formResponses: e.node.formResponses?.edges?.map((fr) => ({
                      questionConfigurationId:
                        fr?.node?.questionConfiguration.id ?? "",
                      stringValue: fr?.node?.stringValue,
                      booleanValue: fr?.node?.booleanValue,
                      transportationTypeId: fr?.node?.transportationType?.id,
                    })),
                  },
                ]
              : []
          ) ?? [],
      };

      removeCurrentPlayer({
        id: event.id,
        eventSignUpRow: input,
      });
    }
  };

  const isGroupEvent =
    event?.maxGuestCount !== 0 ||
    event?.maxMemberCount !== 1 ||
    event?.minMemberCount !== 1 ||
    event?.minGuestCount !== 0;

  const memberRegistrationCount =
    currentSignUp?.registrations?.edges?.reduce((acc, r) => {
      return r?.node?.type === "MEMBER" ? acc + 1 : acc;
    }, 0) || 0;

  const nonMemberHoldRegistrationCount =
    currentSignUp?.registrations?.edges?.reduce((acc, r) => {
      return r?.node?.type === "MEMBER" && r?.node?.registrant?.id
        ? acc + 1
        : acc;
    }, 0) || 0;

  const registrationHasMultipleMembers = memberRegistrationCount > 1;

  const isClosed = ["CLOSED", "COMPLETE"].includes(accessStatusForClient || "");

  const currentDate = new Date();
  const currentDateTime = format(currentDate, "yyyy-MM-dd HH:mm:ss");

  const formattedCurrentSignupCancelDeadlineAt =
    currentSignupWindow?.cancelDeadlineAt
      ? format(
          convertServerTimeToLocal(currentSignupWindow.cancelDeadlineAt),
          "yyyy-MM-dd HH:mm:ss"
        )
      : null;

  const isInCancelWindowRange = formattedCurrentSignupCancelDeadlineAt
    ? currentDateTime < formattedCurrentSignupCancelDeadlineAt
    : true;

  const cancelRegistrationMessage = `${
    isGroupEvent
      ? t`Leaving this event will cancel the signup for the entire group. Are you sure you want to cancel the entire signup`
      : t`Are you sure you’d like to cancel your registration for ` +
        event.eventSeries.name
  }?${
    isClosed
      ? t`\nSign up is closed, and you won’t be able to sign up again.`
      : ""
  }`;

  const openSignUpStep = (step: EventSignUpProcessSteps) => {
    openEventSignUpModal({ overrideStep: step ?? null });
  };

  const isPayingMember = !!currentSignUp?.registrations?.edges?.find(
    (r) => r?.node?.payingMember?.id === registrantId
  );

  const isPayingForOthers = !!currentSignUp?.registrations?.edges?.some(
    (r) =>
      r?.node?.registrant?.id !== registrantId &&
      r?.node?.payingMember?.id === registrantId
  );

  const eventHasForm = useAtomValue(EventDetailEventHasFormAtom);

  // Charges are sourced from the windows the client is eligible for, merged and
  // de-duped across windows (backend already excludes HIDDEN for patrons).
  const eligibleCharges = mergeEligibleWindowCharges(
    event.clientEligibleSignUpWindows
  );

  const eventHasPayments = eligibleCharges.length > 0;

  const isMemberOwner = currentSignUp?.ownerClient?.id === registrantId;
  const isCurrentProfileRegistered = currentSignUp?.registrations?.edges?.find(
    (r) => r?.node?.registrant?.id === registrantId
  );

  const currentMemberCanLeaveEvent = nonMemberHoldRegistrationCount > 1;

  const selectableChargeCount = eligibleCharges.filter(
    (prc) => prc.memberControl === "SELECTABLE"
  ).length;

  const showEditPaymentOption =
    selectableChargeCount && selectableChargeCount > 1;

  const showMoreOptions =
    eventHasForm || (eventHasPayments && showEditPaymentOption);

  const isRemoveMeDisabled =
    !isInCancelWindowRange ||
    (isPayingForOthers && !currentMemberCanLeaveEvent);

  const showRemoveMeButton =
    isGroupEvent &&
    registrationHasMultipleMembers &&
    currentMemberCanLeaveEvent &&
    isCurrentProfileRegistered;
  const canCancelSignUp = currentSignUp?.canDelete === true;

  return (
    <>
      <VStack gap={2} my={6}>
        {canCancelSignUp ? (
          <Button
            accessibilityLabel={
              isGroupEvent ? t`Cancel Entire Signup` : t`Cancel Signup`
            }
            width="100%"
            variant="danger"
            leftIcon={<TrashIcon />}
            onPress={() => setIsLeaveEventModalOpen(true)}
          >
            {isGroupEvent ? t`Cancel Entire Signup` : t`Cancel Signup`}
          </Button>
        ) : null}

        {showRemoveMeButton ? (
          <Button
            accessibilityLabel={t`Remove from Sign Up Button`}
            width="100%"
            variant="secondary"
            leftIcon={<LogOutIcon />}
            onPress={() => setIsRemoveMeFromEventModalOpen(true)}
            isDisabled={isRemoveMeDisabled}
          >
            {t`Remove Me from Signup`}
          </Button>
        ) : null}

        {isGroupEvent && !showMoreOptions ? (
          <Button
            testID="edit-group-button"
            accessibilityLabel={t`Edit Sign Up Button`}
            width="100%"
            variant="secondary"
            leftIcon={<EditIcon />}
            onPress={() => openSignUpStep("PEOPLE")}
            isDisabled={
              !isInCancelWindowRange || (!isPayingMember && !isMemberOwner)
            }
          >
            <Text variant="mainBaseSemiBold">{t`Edit Group`}</Text>
          </Button>
        ) : null}

        {showMoreOptions ? (
          <Menu
            openPosition="bottom-start"
            verticalOffset={8}
            overrideIsOpen={isMoreMenuOpen}
            onClose={() => setIsMoreMenuOpen(false)}
            trigger={({ isOpen }) => (
              <Button
                variant="secondary"
                leftIcon={<KebabVerticalIcon />}
                width="100%"
                accessibilityLabel={t`More Options`}
                accessibilityState={{ expanded: isOpen }}
                onPress={() => setIsMoreMenuOpen(true)}
              >
                {t`More Options`}
              </Button>
            )}
            content={({ closeMenu }) => (
              <Menu.Content size="matchTrigger">
                <VStack accessibilityRole="menu">
                  {isGroupEvent ? (
                    <Button
                      accessibilityLabel={t`Edit Group`}
                      role="menuitem"
                      variant="white"
                      onPress={() => closeMenu(() => openSignUpStep("PEOPLE"))}
                      label={t`Edit Group`}
                    />
                  ) : null}

                  {eventHasForm ? (
                    <Button
                      accessibilityLabel={t`Edit Form`}
                      role="menuitem"
                      variant="white"
                      onPress={() => closeMenu(() => openSignUpStep("FORM"))}
                      label={t`Edit Form`}
                    />
                  ) : null}

                  {eventHasPayments && showEditPaymentOption ? (
                    <Button
                      accessibilityLabel={t`Edit Payments`}
                      role="menuitem"
                      variant="white"
                      onPress={() =>
                        closeMenu(() =>
                          openSignUpStep("PRIMARY_CHARGE_SELECTION")
                        )
                      }
                      label={t`Edit Payments`}
                    />
                  ) : null}
                </VStack>
              </Menu.Content>
            )}
          />
        ) : null}
      </VStack>
      <PopUpModal
        isOpen={isLeaveEventModalOpen}
        upperTitle={t`Cancel Registration`}
        content={
          <VStack>
            <Text color="text-secondary">{cancelRegistrationMessage}</Text>
          </VStack>
        }
        confirmText={t`Cancel Signup`}
        cancelText={t`Cancel`}
        onClose={(confirm: boolean) => {
          if (confirm) {
            handleDeleteRegistration();
          } else {
            setIsLeaveEventModalOpen(false);
          }
        }}
      />
      <PopUpModal
        isOpen={isRemoveMeFromEventModalOpen}
        upperTitle={t`Remove Me From Registration`}
        content={
          <VStack>
            <Text color="text-secondary">
              {t`Are you sure you’d like to remove yourself from ${event.eventSeries.name}?`}
            </Text>
          </VStack>
        }
        confirmText={t`Remove Me`}
        cancelText={t`Cancel`}
        onClose={(confirm: boolean) => {
          if (confirm) {
            handleRemoveFromSignUp();
          } else {
            setIsRemoveMeFromEventModalOpen(false);
          }
        }}
      />
    </>
  );
};

export const EventDetailActionFragmentString = graphql`
  fragment EventDetailActions_event on Event {
    id

    memberSignUpProxyAllowed
    accessStatusForClient(clientId: $memberId)
    signUpStatusForClient(clientId: $memberId)
    maxGuestCount
    maxMemberCount
    minMemberCount
    minGuestCount
    unknownGuestsAllowed
    eventSeries {
      name
      facility {
        id
      }
    }
    clientEligibleSignUpWindows(clientId: $memberId) {
      closeDaysAhead
      closeTime
      closesAt
      openTime
      opensAt
      cancelDeadlineAt
      cancelDeadlineDaysAhead
      cancelDeadlineTime
      charges {
        edges {
          memberControl
          node {
            id
          }
        }
      }
    }
    ...useCurrentSignUp_event
  }
`;
